import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { ChevronIcon } from 'configurator-ui-shared';

import { useConfiguratorDispatch, useRootSelector } from '../../../store';
import { ItemProperties } from '../../../types';
import {
  deleteConfig,
  selectActiveConfigId,
  selectAllConfigSnapshots,
  selectCanDeleteConfig,
  selectConfigIds,
  selectMaxConfigs,
  switchConfig
} from '../store/multiConfigSlice';

function getConfigLabel(properties: ItemProperties | undefined, index: number) {
  const fallback = `Configuration ${index + 1}`;
  if (!properties) return fallback;

  const nameField = Object.values(properties.fields).find(
    field => field.display_name === 'First Name'
  );
  if (!nameField?.value) return fallback;

  const [firstName, lastName] = nameField.value.split('\n');
  const name = [firstName, lastName].filter(Boolean).join(' ');
  return name || fallback;
}

export function ConfigSessionSwitcher() {
  const dispatch = useConfiguratorDispatch();
  const configIds = useRootSelector(selectConfigIds);
  const snapshots = useRootSelector(selectAllConfigSnapshots);
  const activeConfigId = useRootSelector(selectActiveConfigId);
  const canDelete = useRootSelector(selectCanDeleteConfig);
  const maxConfigs = useRootSelector(selectMaxConfigs);

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // "Configure Another" clones the active configuration in place, so nothing
  // else on screen visibly changes - pulse the dropdown toggle's border
  // whenever the configuration count goes up, as a signal that it worked.
  const previousConfigCount = useRef(configIds.length);
  const [isPulsing, setIsPulsing] = useState(false);

  useEffect(() => {
    if (configIds.length > previousConfigCount.current) {
      setIsPulsing(true);
      const timeout = setTimeout(() => setIsPulsing(false), 800);
      previousConfigCount.current = configIds.length;
      return () => clearTimeout(timeout);
    }
    previousConfigCount.current = configIds.length;
  }, [configIds.length]);

  const countLabel =
    maxConfigs !== null
      ? `Configurations ${configIds.length}/${maxConfigs} max`
      : `Configurations: ${configIds.length}`;

  // Below the switcher-worthy 2+ configurations, still surface the count
  // (e.g. "Configurations 1/12 max") - useful on its own once a product has
  // a configured minimum, so the shopper can see progress toward it before
  // there's anything to switch between.
  if (configIds.length <= 1) {
    return (
      <div
        className="mb-2 text-sm text-gray-500"
        data-testid="config-count-indicator"
      >
        {countLabel}
      </div>
    );
  }

  const activeIndex = configIds.indexOf(activeConfigId ?? '');
  const activeSnapshot = snapshots.find(({ id }) => id === activeConfigId);

  return (
    <div
      className="relative mb-2"
      ref={containerRef}
      data-testid="config-session-switcher"
    >
      <div
        className="mb-1 text-sm text-gray-500"
        data-testid="config-count-indicator"
      >
        {countLabel}
      </div>
      <button
        type="button"
        className={clsx(
          'bg-secondary-button text-secondary-button-text flex w-full items-center justify-between gap-2 rounded-[50px] px-3 py-2',
          isPulsing && 'animate-config-pulse'
        )}
        style={{ border: '1px solid currentColor' }}
        onClick={() => setIsOpen(open => !open)}
        data-testid="config-dropdown-toggle"
      >
        <span className="flex items-center gap-2">
          {activeSnapshot?.properties.style?.thumbnail_path && (
            <img
              src={activeSnapshot.properties.style.thumbnail_path}
              className="h-[24px] w-[24px] rounded object-scale-down object-center"
              alt=""
            />
          )}
          {getConfigLabel(activeSnapshot?.properties, activeIndex)}
        </span>
        <ChevronIcon direction={isOpen ? 'up' : 'down'} />
      </button>

      {isOpen && (
        <ul
          className="absolute z-10 mt-1 w-full overflow-hidden rounded-[20px] bg-white shadow"
          style={{
            border: '1px solid var(--nb-secondary-button-text, #014876)'
          }}
        >
          {snapshots.map(({ id: configId, properties }, index) => {
            const isActive = configId === activeConfigId;

            return (
              <li
                key={configId}
                className={clsx(
                  'flex items-center justify-between gap-2 px-3 py-2',
                  isActive && 'bg-gray-100'
                )}
              >
                <button
                  type="button"
                  className="flex flex-1 items-center gap-2 text-left"
                  onClick={() => {
                    dispatch(switchConfig(configId));
                    setIsOpen(false);
                  }}
                  data-testid="config-option-button"
                >
                  <span
                    className="w-[22px] flex-shrink-0 text-left text-gray-500"
                    data-testid="config-option-index"
                  >
                    {index + 1}.
                  </span>
                  {properties.style?.thumbnail_path && (
                    <img
                      src={properties.style.thumbnail_path}
                      className="h-[24px] w-[24px] rounded object-scale-down object-center"
                      alt=""
                    />
                  )}
                  {getConfigLabel(properties, index)}
                </button>
                {canDelete && (
                  <button
                    type="button"
                    aria-label={`Delete Configuration ${index + 1}`}
                    onClick={() => dispatch(deleteConfig(configId))}
                    data-testid="config-option-delete-button"
                  >
                    &times;
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
